import React, { useState } from 'react';
import { motion } from 'framer-motion';

import { PageMeta } from '../../components';

const c = {
  teal: '#1A9E85',
  tealDark: '#157A68',
  tealLight: 'rgba(26,158,133,0.1)',
  tealBorder: 'rgba(26,158,133,0.25)',
  navy: '#0F2033',
  text: '#1B2B3A',
  textMid: '#4A5E6E',
  textLight: '#7A8C99',
  bg: '#F5F9FC',
  border: '#DDE7EE',
};

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
};
const stagger = { visible: { transition: { staggerChildren: 0.08 } } };

const CATEGORIES = [
  { icon: '🚐', name: 'Cargo Vans', desc: 'Transit, Sprinter, ProMaster', key: 'cargo-van' },
  { icon: '🚛', name: 'Cube Trucks', desc: '10ft – 26ft box trucks', key: 'cube-truck' },
  { icon: '🛻', name: 'Work Trucks', desc: 'F-250 to F-550, Ram 2500–5500', key: 'work-truck' },
  { icon: '❄️', name: 'Refrigerated', desc: 'Reefer vans and trucks', key: 'refrigerated' },
  { icon: '🏗️', name: 'Flatbeds', desc: 'Flatdeck, dump, service body', key: 'flatbed' },
  {
    icon: '⚡',
    name: 'Electric Fleet',
    desc: 'E-Transit, eSprinter, BrightDrop',
    key: 'electric-fleet',
  },
  { icon: '🚌', name: 'Passenger Vans', desc: '12–15 passenger shuttles', key: 'passenger-van' },
  {
    icon: '🔧',
    name: 'Service Vehicles',
    desc: 'Utility, bucket, service body',
    key: 'service-vehicle',
  },
];

const MAKES = [
  'All Makes',
  'Ford',
  'RAM',
  'GMC',
  'Chevrolet',
  'Mercedes-Benz',
  'Freightliner',
  'International',
  'Isuzu',
  'Hino',
];
const PROVINCES = ['All Provinces', 'ON', 'BC', 'AB', 'QC', 'MB', 'SK', 'NS', 'NB', 'NL', 'PE'];
const PRICE_RANGES = [
  'Any Price',
  'Under $30K',
  '$30K – $60K',
  '$60K – $100K',
  '$100K – $200K',
  'Over $200K',
];

const PRICE_MAP = {
  'Under $30K': { price_min: 0, price_max: 30000 },
  '$30K – $60K': { price_min: 30000, price_max: 60000 },
  '$60K – $100K': { price_min: 60000, price_max: 100000 },
  '$100K – $200K': { price_min: 100000, price_max: 200000 },
  'Over $200K': { price_min: 200000 },
};

function buildSearchUrl({ condition, make, price, province, category }) {
  const params = new URLSearchParams();
  params.set('pub_vehicleCategory', 'commercial');
  if (category) params.set('pub_commercialType', category);
  if (condition && condition !== 'All') params.set('pub_condition', condition.toLowerCase());
  if (make && make !== 'All Makes') params.set('pub_make', make);
  if (province && province !== 'All Provinces') params.set('pub_province', province);
  if (price && PRICE_MAP[price]) {
    const { price_min, price_max } = PRICE_MAP[price];
    if (price_min !== undefined) params.set('price_min', price_min * 100); // Sharetribe uses cents
    if (price_max !== undefined) params.set('price_max', price_max * 100);
  }
  return `/s?${params.toString()}`;
}

export default function CommercialPage() {
  const [condition, setCondition] = useState('All');
  const [make, setMake] = useState('All Makes');
  const [price, setPrice] = useState('Any Price');
  const [province, setProvince] = useState('All Provinces');
  const [activeCategory, setActiveCategory] = useState(null);

  const searchUrl = buildSearchUrl({ condition, make, price, province, category: activeCategory });

  const s = {
    page: {
      background: c.bg,
      minHeight: '100vh',
      fontFamily: 'DM Sans, -apple-system, sans-serif',
    },
    hero: {
      background: 'linear-gradient(135deg, #0F2033 0%, #1a3550 55%, #0d2a1f 100%)',
      padding: '52px 24px 44px',
      position: 'relative',
      overflow: 'hidden',
    },
    heroEyebrow: {
      color: c.teal,
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: '0.12em',
      textTransform: 'uppercase',
      marginBottom: 10,
    },
    heroTitle: {
      color: '#fff',
      fontSize: 'clamp(26px, 4vw, 38px)',
      fontWeight: 800,
      lineHeight: 1.15,
      marginBottom: 10,
    },
    heroSub: {
      color: 'rgba(255,255,255,0.62)',
      fontSize: 15,
      lineHeight: 1.65,
      maxWidth: 520,
      marginBottom: 28,
    },
    statRow: { display: 'flex', gap: 32, flexWrap: 'wrap' },
    stat: { textAlign: 'left' },
    statNum: { color: c.teal, fontSize: 22, fontWeight: 800 },
    statLabel: { color: 'rgba(255,255,255,0.45)', fontSize: 11, marginTop: 2 },
    filterBox: {
      background: '#fff',
      borderBottom: `1px solid ${c.border}`,
      padding: '16px 24px',
      display: 'flex',
      gap: 10,
      flexWrap: 'wrap',
      alignItems: 'center',
    },
    filterLabel: {
      fontSize: 11,
      fontWeight: 700,
      color: c.textMid,
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
      marginRight: 4,
    },
    condBtn: active => ({
      padding: '7px 16px',
      borderRadius: 20,
      border: `1px solid ${active ? c.navy : c.border}`,
      background: active ? c.navy : '#fff',
      color: active ? '#fff' : c.textMid,
      fontSize: 12,
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.15s',
    }),
    select: {
      padding: '7px 12px',
      borderRadius: 8,
      border: `1px solid ${c.border}`,
      fontSize: 12,
      color: c.text,
      background: '#fff',
      cursor: 'pointer',
    },
    searchBtn: {
      marginLeft: 'auto',
      padding: '8px 22px',
      borderRadius: 8,
      border: 'none',
      background: c.teal,
      color: '#fff',
      fontWeight: 700,
      fontSize: 13,
      cursor: 'pointer',
    },
    sectionTitle: {
      fontSize: 11,
      fontWeight: 700,
      color: c.textMid,
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
      marginBottom: 12,
    },
    catGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
      gap: 12,
      padding: '20px 24px 4px',
    },
    catCard: active => ({
      background: active ? '#f0fbf8' : '#fff',
      border: `1.5px solid ${active ? c.teal : c.border}`,
      borderRadius: 12,
      padding: '14px 12px',
      textAlign: 'center',
      cursor: 'pointer',
      transition: 'all 0.15s',
    }),
    catIcon: { fontSize: 26, marginBottom: 6, display: 'block' },
    catName: { fontSize: 12, fontWeight: 700, color: c.text },
    catDesc: { fontSize: 10, color: c.textLight, marginTop: 2 },
    pillRow: { padding: '0 24px 20px', display: 'flex', flexWrap: 'wrap', gap: 8 },
    pill: {
      background: 'rgba(26,158,133,0.1)',
      border: '1px solid rgba(26,158,133,0.25)',
      color: c.teal,
      fontSize: 11,
      fontWeight: 600,
      padding: '4px 12px',
      borderRadius: 20,
    },
    ctaBar: {
      margin: '8px 24px 32px',
      background: c.navy,
      borderRadius: 14,
      padding: '28px 32px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 20,
      flexWrap: 'wrap',
    },
    ctaText: { color: '#fff', fontSize: 16, fontWeight: 700, marginBottom: 6 },
    ctaSub: { color: 'rgba(255,255,255,0.55)', fontSize: 13 },
    ctaBtnPrimary: {
      padding: '11px 22px',
      borderRadius: 8,
      border: 'none',
      background: c.teal,
      color: '#fff',
      fontWeight: 700,
      fontSize: 13,
      cursor: 'pointer',
    },
    ctaBtnSecondary: {
      padding: '11px 22px',
      borderRadius: 8,
      border: '1px solid rgba(255,255,255,0.2)',
      background: 'rgba(255,255,255,0.08)',
      color: '#fff',
      fontWeight: 600,
      fontSize: 13,
      cursor: 'pointer',
      marginLeft: 10,
    },
    trustRow: { display: 'flex', flexWrap: 'wrap', gap: 12, padding: '0 24px 32px' },
    trustCard: {
      flex: '1 1 200px',
      background: '#fff',
      border: `1px solid ${c.border}`,
      borderRadius: 12,
      padding: '16px 18px',
      display: 'flex',
      gap: 12,
      alignItems: 'flex-start',
    },
    trustIcon: { fontSize: 22, marginTop: 2 },
    trustTitle: { fontSize: 13, fontWeight: 700, color: c.text, marginBottom: 3 },
    trustDesc: { fontSize: 12, color: c.textMid, lineHeight: 1.5 },
    faqBox: { padding: '0 24px 40px' },
    faq: {
      background: '#fff',
      border: `1px solid ${c.border}`,
      borderRadius: 10,
      padding: '14px 18px',
      marginBottom: 8,
    },
    faqQ: { fontSize: 13, fontWeight: 700, color: c.text, marginBottom: 4 },
    faqA: { fontSize: 12, color: c.textMid, lineHeight: 1.6 },
  };

  return (
    <>
      <PageMeta
        path="/commercial"
        title="Commercial & Fleet Vehicles in Canada"
        description="Browse cargo vans, trucks, flatbeds and specialized vehicles. Search by type, make, price and province."
      />
      <div style={s.page}>
        {/* HERO */}
        <motion.div style={s.hero} initial="hidden" animate="visible" variants={stagger}>
          <motion.div variants={fadeUp} style={s.heroEyebrow}>
            Commercial &amp; Fleet Vehicles · Canada
          </motion.div>
          <motion.h1 variants={fadeUp} style={s.heroTitle}>
            Work Vehicles, Vans &amp; Fleet
            <br />
            <span style={{ color: c.teal }}>No Dealership Markup.</span>
          </motion.h1>
          <motion.p variants={fadeUp} style={s.heroSub}>
            Buy direct from verified Canadian businesses and private sellers. Cargo vans, cube
            trucks, flatbeds, reefers — new and used with zero dealer fees.
          </motion.p>
          <motion.div variants={fadeUp} style={s.pillRow}>
            {[
              'Ford Transit',
              'RAM ProMaster',
              'Mercedes Sprinter',
              'GMC Savana',
              'Isuzu NPR',
              'E-Transit Electric',
              'Freightliner',
              'Cube Trucks',
            ].map(v => (
              <span key={v} style={s.pill}>
                {v}
              </span>
            ))}
          </motion.div>
          <motion.div variants={fadeUp} style={s.statRow}>
            {[
              ['100%', 'Verified Sellers'],
              ['$0', 'Dealer Fees'],
              ['Free', 'VIN History Check'],
              ['AI', 'Market Valuation'],
            ].map(([n, l]) => (
              <div key={l} style={s.stat}>
                <div style={s.statNum}>{n}</div>
                <div style={s.statLabel}>{l}</div>
              </div>
            ))}
          </motion.div>
        </motion.div>

        {/* FILTER BAR */}
        <div style={s.filterBox}>
          <span style={s.filterLabel}>Filter</span>
          {['All', 'New', 'Used'].map(c_ => (
            <button key={c_} style={s.condBtn(condition === c_)} onClick={() => setCondition(c_)}>
              {c_}
            </button>
          ))}
          <select style={s.select} value={make} onChange={e => setMake(e.target.value)}>
            {MAKES.map(m => (
              <option key={m}>{m}</option>
            ))}
          </select>
          <select style={s.select} value={price} onChange={e => setPrice(e.target.value)}>
            {PRICE_RANGES.map(p => (
              <option key={p}>{p}</option>
            ))}
          </select>
          <select style={s.select} value={province} onChange={e => setProvince(e.target.value)}>
            {PROVINCES.map(p => (
              <option key={p}>{p}</option>
            ))}
          </select>
          <a
            href={searchUrl}
            style={{ ...s.searchBtn, textDecoration: 'none', display: 'inline-block' }}
          >
            Search Vehicles →
          </a>
        </div>

        {/* CATEGORY STRIP */}
        <div style={{ padding: '20px 24px 0' }}>
          <div style={s.sectionTitle}>Browse by vehicle type</div>
        </div>
        <div style={s.catGrid}>
          {CATEGORIES.map(cat => (
            <motion.div
              key={cat.key}
              style={s.catCard(activeCategory === cat.key)}
              onClick={() => setActiveCategory(activeCategory === cat.key ? null : cat.key)}
              whileHover={{ y: -2 }}
            >
              <span style={s.catIcon}>{cat.icon}</span>
              <div style={s.catName}>{cat.name}</div>
              <div style={s.catDesc}>{cat.desc}</div>
            </motion.div>
          ))}
        </div>

        {/* SELECTED CATEGORY SEARCH CTA */}
        {activeCategory && (
          <div style={{ padding: '16px 24px 0', display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 13, color: c.textMid }}>
              Showing:{' '}
              <strong style={{ color: c.text }}>
                {CATEGORIES.find(c => c.key === activeCategory)?.name}
              </strong>
            </span>
            <a
              href={searchUrl}
              style={{
                padding: '7px 18px',
                borderRadius: 8,
                background: c.teal,
                color: '#fff',
                fontWeight: 700,
                fontSize: 12,
                textDecoration: 'none',
              }}
            >
              View Listings →
            </a>
            <button
              onClick={() => setActiveCategory(null)}
              style={{
                background: 'none',
                border: 'none',
                color: c.textLight,
                fontSize: 12,
                cursor: 'pointer',
              }}
            >
              Clear
            </button>
          </div>
        )}

        {/* TRUST BADGES */}
        <div style={{ padding: '28px 24px 12px' }}>
          <div style={s.sectionTitle}>Why buy commercial vehicles here</div>
        </div>
        <div style={s.trustRow}>
          {[
            [
              '✅',
              'Every Seller Verified',
              'Businesses and private sellers go through ID verification before listing. No fake postings.',
            ],
            [
              '🔍',
              'Free VIN History Check',
              'Every listing includes a free stolen vehicle and history report — see accidents, owners, and odometer records.',
            ],
            [
              '🤖',
              'AI Market Valuation',
              'Ask Sunny what a fair price is for any commercial vehicle in Canada. No guessing.',
            ],
            [
              '💬',
              'Direct Contact, No Middleman',
              'Message the seller directly. No dealer call centre, no commission, no upsells.',
            ],
            [
              '⚡',
              'EV Fleet Rebates',
              'Get notified about iZEV and provincial incentives for electric commercial vehicles.',
            ],
            [
              '🏦',
              'Commercial Financing',
              'Pre-qualify for commercial vehicle financing with no hard credit pull.',
            ],
          ].map(([icon, title, desc]) => (
            <div key={title} style={s.trustCard}>
              <span style={s.trustIcon}>{icon}</span>
              <div>
                <div style={s.trustTitle}>{title}</div>
                <div style={s.trustDesc}>{desc}</div>
              </div>
            </div>
          ))}
        </div>

        {/* SELLER CTA */}
        <div style={s.ctaBar}>
          <div>
            <div style={s.ctaText}>Selling a commercial vehicle or fleet?</div>
            <div style={s.ctaSub}>
              List free. Reach verified Canadian business buyers. No dealer cut.
            </div>
          </div>
          <div>
            <a href="/l/new" style={{ ...s.ctaBtnPrimary, textDecoration: 'none' }}>
              List Your Vehicle Free
            </a>
            <a
              href="mailto:sales@skipthedealerships.ca?subject=Fleet Inquiry"
              style={{ ...s.ctaBtnSecondary, textDecoration: 'none' }}
            >
              Fleet Inquiry →
            </a>
          </div>
        </div>

        {/* FAQ */}
        <div style={{ padding: '0 24px 12px' }}>
          <div style={s.sectionTitle}>Common questions</div>
        </div>
        <div style={s.faqBox}>
          {[
            [
              'Can I list an entire fleet of vehicles?',
              "Yes — contact us at sales@skipthedealerships.ca and we'll set up a business seller account so you can list multiple vehicles at once.",
            ],
            [
              'Are commercial vehicle listings free?',
              'Listing is free. SkipTheDealerships.ai charges no listing fee and no dealer commission. You keep 100% of your sale price.',
            ],
            [
              'What commercial vehicle types can I list?',
              'Any work vehicle: cargo vans, cube trucks, flat decks, dump trucks, reefers, service bodies, crew cabs, buses, trailers, and heavy equipment.',
            ],
            [
              'How does the AI market valuation work for commercial vehicles?',
              'Ask Sunny (our AI assistant) for a market value estimate. She uses Canadian pricing data, mileage, condition, and current demand to give you a realistic range.',
            ],
            [
              'Can I finance a commercial vehicle through this platform?',
              'Yes — click "Get Financing" on any listing or use our pre-qualification tool. We work with Canadian commercial lenders for trucks, vans, and fleet vehicles.',
            ],
          ].map(([q, a]) => (
            <div key={q} style={s.faq}>
              <div style={s.faqQ}>{q}</div>
              <div style={s.faqA}>{a}</div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
